import React from 'react';
import { Organization } from '../../../../types/organization.types';
import { DataField } from './DataField';
import { SectionCard, FieldGrid } from './SectionComponents';

interface CRMSectionProps {
  selectedOrg: Organization;
  onboardingSetup: any;
  onboardingData: any;
  asTab?: boolean;
}

export const CRMSection: React.FC<CRMSectionProps> = ({ selectedOrg, onboardingData }) => {
  const crmData = onboardingData.crm;
  const crmEntries = crmData?.crm_entries || [];
  const entryAcknowledged = crmEntries.some((e: any) => e?.ai_disclaimer_acknowledged);
  const hasConnectedCrm = Boolean(
    selectedOrg?.rollout &&
    Object.values(selectedOrg.rollout).some(
      (config: any) =>
        config && typeof config === 'object' && 'connected' in config && config.connected
    )
  );
  // Accept legacy camelCase from old Firestore onboarding.
  const consentAcknowledged =
    Boolean(onboardingData?.crm_consent_acknowledged ?? onboardingData?.crmConsentAcknowledged) ||
    entryAcknowledged ||
    hasConnectedCrm;
  const consentAt = (onboardingData?.crm_consent_acknowledged_at ??
    onboardingData?.crmConsentAcknowledgedAt) as string | undefined;

  if (!crmEntries.length) {
    return <p className="text-gray-500 dark:text-gray-400 text-sm">No CRM data available</p>;
  }

  return (
    <div className="space-y-4">
      {crmEntries.map((entry: any, index: number) => {
        const crmName = entry.crm_type?.toUpperCase() || 'CRM';
        const displayName = entry.crm_other_name ? `${crmName} (${entry.crm_other_name})` : crmName;

        return (
          <SectionCard key={index} title={displayName}>
            <FieldGrid>
              <DataField
                label="AI CRM Disclaimer Acknowledged"
                value={entry.ai_disclaimer_acknowledged ? 'Yes' : 'No'}
              />
              {entry.login_url && (
                <DataField label="Login URL" value={entry.login_url} type="link" />
              )}
              {entry.two_fa_contact_name ? (
                <>
                  <DataField label="2FA Contact Name" value={entry.two_fa_contact_name} />
                  <DataField
                    label="2FA Contact Email"
                    value={entry.two_fa_contact_email}
                    type="email"
                  />
                  <DataField
                    label="2FA Contact Phone"
                    value={entry.two_fa_contact_phone}
                    type="phone"
                  />
                </>
              ) : (
                // Orgs onboarded before the 2FA split saved one free-text value.
                entry.two_fa_contact && (
                  <DataField label="2FA Contact" value={entry.two_fa_contact} />
                )
              )}
            </FieldGrid>

            {/* Note about sensitive data */}
            {(entry.super_admin_api_key ||
              entry.super_admin_password ||
              entry.super_admin_username) && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 italic">
                API Key and Password details sent via webhook
              </p>
            )}
          </SectionCard>
        );
      })}

      <SectionCard title="Compliance Acknowledgement">
        <FieldGrid>
          <DataField
            label="AI Contact Consent"
            value={consentAcknowledged ? 'Acknowledged' : 'Not acknowledged'}
          />
          {consentAcknowledged && consentAt && (
            <DataField label="Acknowledged At" value={new Date(consentAt).toLocaleString()} />
          )}
        </FieldGrid>
      </SectionCard>
    </div>
  );
};
